import {
    findIbicCategoryKey,
    translateToSwedish,
    IBIC_CATEGORIES_SV
} from "../constants/ibicCategories";

export type SegmentType = "text" | "ibic-category" | "ibic-code";

export interface ParsedSegment {
    type: SegmentType;
    content: string;
    value?: string; // The extracted IBIC value
    label?: string; // The label (e.g., "IBIC", "IBIC-kod")
    lineIndex: number;
}

export interface ParsedNote {
    segments: ParsedSegment[];
    hasIbic: boolean;
    detectedLanguage: "en" | "sv";
}

// Regex patterns for detecting IBIC lines in both languages
const IBIC_CATEGORY_PATTERN = /^(IBIC):\s*(.+)$/i;
const IBIC_CODE_PATTERN_EN = /^(IBIC Code):\s*(.+)$/i;
const IBIC_CODE_PATTERN_SV = /^(IBIC-kod):\s*(.+)$/i;

// Detect language from the note text
function detectLanguage(text: string): "en" | "sv" {
    // Check for Swedish-specific patterns
    if (
        text.includes("IBIC-kod:") ||
        text.includes("Anteckningstyp:") ||
        text.includes("Bedömning:") ||
        text.includes("Förslag till dokumentation") ||
        text.includes("Datum:") ||
        text.includes("Tid:")
    ) {
        return "sv";
    }
    return "en";
}

/**
 * Parse note text to identify IBIC lines and split into segments
 */
export function parseNoteForIbic(text: string): ParsedNote {
    const lines = text.split("\n");
    const segments: ParsedSegment[] = [];
    let textBuffer = "";
    let hasIbic = false;
    const detectedLanguage = detectLanguage(text);

    lines.forEach((line, lineIndex) => {
        // Try to match IBIC category line
        const categoryMatch = line.match(IBIC_CATEGORY_PATTERN);
        if (categoryMatch) {
            // Flush text buffer if exists
            if (textBuffer) {
                segments.push({
                    type: "text",
                    content: textBuffer,
                    lineIndex: -1
                });
                textBuffer = "";
            }
            segments.push({
                type: "ibic-category",
                content: line,
                label: categoryMatch[1],
                value: categoryMatch[2].trim(),
                lineIndex
            });
            hasIbic = true;
            return;
        }

        // Try to match IBIC code line (English)
        const codeMatchEn = line.match(IBIC_CODE_PATTERN_EN);
        if (codeMatchEn) {
            if (textBuffer) {
                segments.push({
                    type: "text",
                    content: textBuffer,
                    lineIndex: -1
                });
                textBuffer = "";
            }
            segments.push({
                type: "ibic-code",
                content: line,
                label: codeMatchEn[1],
                value: codeMatchEn[2].trim(),
                lineIndex
            });
            hasIbic = true;
            return;
        }

        // Try to match IBIC code line (Swedish)
        const codeMatchSv = line.match(IBIC_CODE_PATTERN_SV);
        if (codeMatchSv) {
            if (textBuffer) {
                segments.push({
                    type: "text",
                    content: textBuffer,
                    lineIndex: -1
                });
                textBuffer = "";
            }
            segments.push({
                type: "ibic-code",
                content: line,
                label: codeMatchSv[1],
                value: codeMatchSv[2].trim(),
                lineIndex
            });
            hasIbic = true;
            return;
        }

        // Regular text line - add to buffer
        textBuffer += (textBuffer ? "\n" : "") + line;
    });

    // Flush remaining text buffer
    if (textBuffer) {
        segments.push({
            type: "text",
            content: textBuffer,
            lineIndex: -1
        });
    }

    return { segments, hasIbic, detectedLanguage };
}

/**
 * Reconstruct note text from segments after modifications
 */
export function reconstructNoteText(
    segments: ParsedSegment[],
    language: "en" | "sv"
): string {
    return segments
        .map(segment => {
            if (segment.type === "text") {
                return segment.content;
            }

            if (segment.type === "ibic-category") {
                const label = "IBIC";
                const value = language === "sv" && segment.value
                    ? getSwedishValue(segment.value)
                    : segment.value;
                return `${label}: ${value}`;
            }

            if (segment.type === "ibic-code") {
                const label = language === "sv" ? "IBIC-kod" : "IBIC Code";
                const value = segment.value || "";
                return `${label}: ${value}`;
            }

            return segment.content;
        })
        .join("\n");
}

/**
 * Get Swedish translation of an IBIC value if available
 */
function getSwedishValue(value: string): string {
    // First try to find the English key
    const englishKey = findIbicCategoryKey(value);
    if (englishKey) {
        return translateToSwedish(englishKey);
    }
    return value;
}

/**
 * Update a specific segment's value
 */
export function updateSegmentValue(
    segments: ParsedSegment[],
    segmentIndex: number,
    newValue: string
): ParsedSegment[] {
    return segments.map((segment, index) => {
        if (index === segmentIndex) {
            return {
                ...segment,
                value: newValue,
                content: `${segment.label}: ${newValue}`
            };
        }
        return segment;
    });
}

/**
 * Extract just the code part from an IBIC value
 * e.g., "d550 (Äta)" -> "d550"
 * e.g., "d550 Eating" -> "d550"
 */
export function extractCodeFromValue(value: string): string {
    const match = value.match(/^([a-zA-Z]\d+[A-Z]?)/);
    return match ? match[1] : "";
}

/**
 * Check if a value is a main category (e.g., "d5 Self-care")
 * vs a specific code (e.g., "d550 Eating")
 */
export function isMainCategory(value: string): boolean {
    const code = extractCodeFromValue(value);
    // Main categories have only letter + single digit (e.g., "d5", "d1")
    return /^[a-zA-Z]\d?$/.test(code);
}
